import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Rocket,
  Key,
  Wrench,
  Package,
  CheckSquare,
  Settings,
} from 'lucide-react'
import { scopesApi, deliverablesApi, dealsApi, presalesDashboardApi } from '@/api/client'
import type { GeneralInfo } from '@/features/scope/types'
import { calculateScopeKnownPercent } from '@/features/scope/utils/mappers'
import type { Deliverable, PresalesTask } from '@/api/types'
import { showToast } from '@/store/uiStore'
import { generateCalendarPDF } from '../utils/calendarExport'

/**
 * Production Planning Calendar
 *
 * PURPOSE:
 * This calendar is designed to share across FrankieOne the upcoming key dates for clients
 * related to Presales and Implementations, so individuals know which projects to focus on
 * and prioritise.
 *
 * DATA SOURCE:
 * - Information captured during the Presales Scoping process
 * - Maintained and updated by the Implementations team
 *
 * KEY DATES TRACKED:
 * - Go-Live Dates: When customers go live with FrankieOne
 * - Production Access: When production keys are delivered to customers
 * - Custom Work Items: Custom development/integration tasks with due dates
 * - Deliverables: Other project deliverables and milestones
 *
 * AUTOMATION:
 * - Automated Slack notifications can be configured to post calendar summaries
 * - Product managers can be tagged for relevant custom work items
 * - Settings accessible via the Settings button in the calendar header
 *
 * PRODUCT MANAGERS:
 * - Vanessa (vanessa@frankieone.com)
 * - Kieran (kieran@frankieone.com)
 * - Jazor (jazor.he@frankieone.com)
 * - Nidhi (nidhi.verma@frankieone.com)
 */

interface CalendarEvent {
  id: string
  dealId: number
  dealName: string
  companyName: string
  type: 'go_live' | 'production_access' | 'custom_work' | 'deliverable' | 'task'
  title: string
  date: Date
  description?: string
  sowKnownPercent?: number
  metadata?: {
    category?: string
    status?: string
    jiraTickets?: Array<{ ticket: string; status: string }>
    assignedPmId?: number  // Product manager assigned to custom work items
    assignedTo?: string    // Person assigned (for tasks)
    template?: string      // Task template type
    isHardDate?: boolean   // Whether this is a firm date commitment
    handedOver?: boolean   // Whether the deal has been handed over to implementations
  }
}

export function CalendarTab() {
  const navigate = useNavigate()
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date())
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')
  const [error, setError] = useState<string | null>(null)
  const [expandedDay, setExpandedDay] = useState<string | null>(null) // dateKey for "+N more" popover
  const [hideHandedOverCustomWork, setHideHandedOverCustomWork] = useState(true)

  // Fetch deals and calendar data on mount
  useEffect(() => {
    async function loadCalendarData() {
      setLoading(true)
      setError(null)
      try {
        // First, fetch all deals (fetch all pages)
        const dealsResponse = await dealsApi.list({ pageSize: 1000 })
        if (!dealsResponse.success || !dealsResponse.data) {
          const errorMsg = dealsResponse.error || 'Failed to fetch deals'
          console.error('Failed to fetch deals:', errorMsg)
          setError(errorMsg)
          setLoading(false)
          return
        }

        const fetchedDeals = dealsResponse.data.items

        if (!fetchedDeals || fetchedDeals.length === 0) {
          setLoading(false)
          return
        }

        const allEvents: CalendarEvent[] = []

        // Fetch scopes for all deals (with timeout)
        const scopePromises = fetchedDeals.map(async (deal: any) => {
          try {
            // Add a 2 second timeout for each API call to prevent hanging
            const timeoutPromise = new Promise<{ success: false }>((resolve) =>
              setTimeout(() => resolve({ success: false }), 2000)
            )
            const scopeResponse = await Promise.race([scopesApi.getByDealId(deal.id), timeoutPromise])
            if (!scopeResponse.success || !('data' in scopeResponse) || !scopeResponse.data) return []

            const generalInfo: GeneralInfo = JSON.parse(scopeResponse.data.general_info || '{}')
            const sowPercent = calculateScopeKnownPercent(scopeResponse.data)

            const baseInfo = {
              dealId: deal.id,
              dealName: deal.hubspot_deal_name || deal.customer_name,
              companyName: deal.customer_name || deal.hubspot_deal_name || 'Unknown',
              sowKnownPercent: sowPercent,
            }

            const result: CalendarEvent[] = []

            // Go-live date
            if (generalInfo.goLiveDate) {
              const date = new Date(generalInfo.goLiveDate)
              if (!isNaN(date.getTime())) {
                result.push({
                  id: `golive-${deal.id}`,
                  ...baseInfo,
                  type: 'go_live',
                  title: `Go Live`,
                  date,
                  description: 'Customer go-live date',
                  metadata: { isHardDate: !!generalInfo.isHardGoLiveDate },
                })
              }
            }

            // Production access date
            if (generalInfo.productionAccessDate) {
              const date = new Date(generalInfo.productionAccessDate)
              if (!isNaN(date.getTime())) {
                result.push({
                  id: `prodaccess-${deal.id}`,
                  ...baseInfo,
                  type: 'production_access',
                  title: `Production Keys`,
                  date,
                  description: 'Production access keys delivery',
                })
              }
            }

            // Custom work items
            if (generalInfo.customWorks && Array.isArray(generalInfo.customWorks)) {
              generalInfo.customWorks.forEach((work) => {
                if (work.dueDate) {
                  const date = new Date(work.dueDate)
                  if (!isNaN(date.getTime())) {
                    result.push({
                      id: `custom-${deal.id}-${work.id}`,
                      ...baseInfo,
                      type: 'custom_work',
                      title: work.name,
                      date,
                      description: work.description,
                      metadata: {
                        category: work.category,
                        status: work.lifecycleStatus,
                        jiraTickets: work.jiraTickets,
                        isHardDate: !!work.isHardDate,
                        // Prefer live scope lifecycle status; fall back to the legacy sticky flag.
                        handedOver: generalInfo.scopeStatus
                          ? generalInfo.scopeStatus === 'handover'
                          : !!deal.sales_handover_completed,
                      },
                    })
                  }
                }
              })
            }

            return result
          } catch (err) {
            console.error(`Failed to load scope for deal ${deal.id}:`, err)
            return []
          }
        })

        // Fetch deliverables for all deals (with timeout)
        const deliverablePromises = fetchedDeals.map(async (deal: any) => {
          try {
            // Add a 2 second timeout for each API call to prevent hanging
            const timeoutPromise = new Promise<{ success: false }>((resolve) =>
              setTimeout(() => resolve({ success: false }), 2000)
            )
            const delivResponse = await Promise.race([deliverablesApi.getByDealId(deal.id), timeoutPromise])
            if (!delivResponse.success || !('data' in delivResponse) || !delivResponse.data) return []

            const result: CalendarEvent[] = delivResponse.data
              .filter((d: Deliverable) => d.due_date)
              .map((d: Deliverable) => {
                const date = new Date(d.due_date!)
                return {
                  id: `deliverable-${d.id}`,
                  dealId: deal.id,
                  dealName: deal.hubspot_deal_name || deal.customer_name,
                  companyName: deal.customer_name || deal.hubspot_deal_name || 'Unknown',
                  type: 'deliverable' as const,
                  title: d.name,
                  date,
                  description: d.description || undefined,
                  metadata: {
                    category: d.category,
                    status: d.status,
                  },
                }
              })

            return result
          } catch (err) {
            console.error(`Failed to load deliverables for deal ${deal.id}:`, err)
            return []
          }
        })

        // Fetch all open tasks with due dates in one call (uses presales_deals for correct names)
        const taskPromise = (async () => {
          try {
            const timeoutPromise = new Promise<{ success: false }>((resolve) =>
              setTimeout(() => resolve({ success: false }), 5000)
            )
            const taskResponse = await Promise.race([presalesDashboardApi.getCalendarTasks(), timeoutPromise])
            if (!taskResponse.success || !('data' in taskResponse) || !taskResponse.data) return []

            return (taskResponse.data as (PresalesTask & { deal_name?: string; company_name?: string })[])
              .filter((t) => t.due_date)
              .map((t) => {
                const date = new Date(t.due_date!)
                const companyName = t.company_name || t.deal_name || 'Unknown'
                return {
                  id: `task-${t.id}`,
                  dealId: t.deal_id ?? 0,
                  dealName: t.deal_name || companyName,
                  companyName,
                  type: 'task' as const,
                  title: t.description,
                  date,
                  description: t.assigned_to ? `Assigned to ${t.assigned_to}` : undefined,
                  metadata: {
                    status: t.status,
                    assignedTo: t.assigned_to || undefined,
                    template: t.template,
                  },
                } satisfies CalendarEvent
              })
          } catch (err) {
            console.error('Failed to load calendar tasks:', err)
            return []
          }
        })()

        const allScopeResults = await Promise.all(scopePromises)
        const allDeliverableResults = await Promise.all(deliverablePromises)
        const taskEvents = await taskPromise

        // Flatten and combine
        allScopeResults.forEach((scopeEvents: any) => {
          if (scopeEvents && scopeEvents.length > 0) {
            allEvents.push(...scopeEvents)
          }
        })

        allDeliverableResults.forEach((delivEvents: any) => {
          if (delivEvents && delivEvents.length > 0) {
            allEvents.push(...delivEvents)
          }
        })

        if (taskEvents.length > 0) {
          allEvents.push(...taskEvents)
        }

        // Deduplicate events by title and date (some events appear in both deliverables and custom work)
        const uniqueEvents = allEvents.reduce((acc, event) => {
          const key = `${event.title}-${event.date.toISOString().split('T')[0]}`
          if (!acc.has(key)) {
            acc.set(key, event)
          }
          return acc
        }, new Map<string, CalendarEvent>())

        const deduplicatedEvents = Array.from(uniqueEvents.values())

        // Sort by date
        deduplicatedEvents.sort((a, b) => a.date.getTime() - b.date.getTime())

        setEvents(deduplicatedEvents)
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to load calendar data'
        console.error('Failed to load calendar data:', err)
        setError(errorMsg)
        showToast('Failed to load calendar events', 'error')
      } finally {
        setLoading(false)
      }
    }

    loadCalendarData()
  }, [])

  // Filter events for selected month
  const monthEvents = useMemo(() => {
    const startOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1)
    const endOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0, 23, 59, 59)

    return events.filter((event) => {
      if (event.date < startOfMonth || event.date > endOfMonth) return false
      if (hideHandedOverCustomWork && event.type === 'custom_work' && event.metadata?.handedOver) return false
      return true
    })
  }, [events, selectedMonth, hideHandedOverCustomWork])

  // Group events by date for calendar view
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>()
    monthEvents.forEach((event) => {
      const dateKey = event.date.toISOString().split('T')[0]
      if (!map.has(dateKey)) {
        map.set(dateKey, [])
      }
      map.get(dateKey)!.push(event)
    })
    return map
  }, [monthEvents])

  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const year = selectedMonth.getFullYear()
    const month = selectedMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startingDayOfWeek = firstDay.getDay()
    const daysInMonth = lastDay.getDate()

    const days: Array<{ date: Date; isCurrentMonth: boolean; events: CalendarEvent[] }> = []

    // Previous month days to fill the week
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i)
      days.push({ date, isCurrentMonth: false, events: [] })
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      const dateKey = date.toISOString().split('T')[0]
      days.push({
        date,
        isCurrentMonth: true,
        events: eventsByDate.get(dateKey) || [],
      })
    }

    // Next month days to complete the grid
    const remainingDays = 42 - days.length // 6 weeks
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day)
      days.push({ date, isCurrentMonth: false, events: [] })
    }

    return days
  }, [selectedMonth, eventsByDate])

  const handlePreviousMonth = () => {
    const prev = new Date(selectedMonth)
    prev.setMonth(prev.getMonth() - 1)
    setSelectedMonth(prev)
  }

  const handleNextMonth = () => {
    const next = new Date(selectedMonth)
    next.setMonth(next.getMonth() + 1)
    setSelectedMonth(next)
  }

  const handleToday = () => {
    setSelectedMonth(new Date())
  }

  // Navigate to the relevant page when clicking an event
  const handleEventClick = useCallback((event: CalendarEvent) => {
    if (event.type === 'task') {
      // Navigate to the person's tab in the presales dashboard
      const assignedTo = event.metadata?.assignedTo
      if (assignedTo) {
        const firstName = assignedTo.split(' ')[0]
        navigate(`/dashboard/presales/${firstName}`)
      } else {
        // No assignee — go to the deal's scope
        navigate(`/scope?session=${event.dealId}`)
      }
    } else {
      // Go-live, production_access, custom_work, deliverable → go to SOW
      navigate(`/scope?session=${event.dealId}`)
    }
  }, [navigate])

  const handleExportPDF = async () => {
    try {
      setLoading(true)
      // Fetch comprehensive events from database (next 2 months)
      const comprehensiveEvents = await fetchComprehensiveEvents()
      generateCalendarPDF(comprehensiveEvents, new Date())
      showToast('Calendar PDF downloaded successfully', 'success')
    } catch (err) {
      console.error('Failed to generate PDF:', err)
      showToast('Failed to generate PDF', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Fetch comprehensive events from database (all deals, next 2 months)
  const fetchComprehensiveEvents = async (): Promise<CalendarEvent[]> => {
    const now = new Date()
    const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const endDate = new Date(now.getFullYear(), now.getMonth() + 3, 0)

    // Fetch all deals
    const dealsResponse = await dealsApi.list({ pageSize: 1000 })
    if (!dealsResponse.success || !dealsResponse.data) {
      return []
    }

    const allEvents: CalendarEvent[] = []

    // Fetch scopes for each deal
    for (const deal of dealsResponse.data.items) {
      try {
        const scopeResponse = await scopesApi.getByDealId(deal.id)
        if (!scopeResponse.success || !scopeResponse.data) continue

        const generalInfo: GeneralInfo = JSON.parse(scopeResponse.data.general_info || '{}')
        const companyName = deal.customer_name || deal.hubspot_deal_name || 'Unknown'

        // Go-live date
        if (generalInfo.goLiveDate) {
          const date = new Date(generalInfo.goLiveDate)
          if (!isNaN(date.getTime()) && date >= startDate && date <= endDate) {
            allEvents.push({
              id: `${deal.id}-golive`,
              dealId: deal.id,
              dealName: deal.hubspot_deal_name || deal.customer_name,
              companyName,
              title: 'Go Live',
              type: 'go_live',
              date,
              metadata: { isHardDate: !!generalInfo.isHardGoLiveDate },
            })
          }
        }

        // Production access date
        if (generalInfo.productionAccessDate) {
          const date = new Date(generalInfo.productionAccessDate)
          if (!isNaN(date.getTime()) && date >= startDate && date <= endDate) {
            allEvents.push({
              id: `${deal.id}-prodkeys`,
              dealId: deal.id,
              dealName: deal.hubspot_deal_name || deal.customer_name,
              companyName,
              title: 'Production Keys',
              type: 'production_access',
              date,
            })
          }
        }

        // Custom work items
        const customWork = JSON.parse(scopeResponse.data.custom_work_json || '[]')
        if (Array.isArray(customWork)) {
          customWork.forEach((item: any, idx: number) => {
            if (item.dueDate) {
              const date = new Date(item.dueDate)
              if (!isNaN(date.getTime()) && date >= startDate && date <= endDate) {
                allEvents.push({
                  id: `${deal.id}-custom-${idx}`,
                  dealId: deal.id,
                  dealName: deal.hubspot_deal_name || deal.customer_name,
                  companyName,
                  title: item.name || 'Custom Work',
                  type: 'custom_work',
                  date,
                  metadata: {
                    jiraTickets: item.jiraTickets || [],
                  },
                })
              }
            }
          })
        }
      } catch (error) {
        console.error(`Error fetching scope for deal ${deal.id}:`, error)
      }

    }

    // Fetch all open tasks with due dates in one call (uses presales_deals for correct names)
    try {
      const taskResponse = await presalesDashboardApi.getCalendarTasks()
      if (taskResponse.success && taskResponse.data) {
        (taskResponse.data as (PresalesTask & { deal_name?: string; company_name?: string })[])
          .filter((t) => t.due_date)
          .forEach((t) => {
            const date = new Date(t.due_date!)
            if (!isNaN(date.getTime()) && date >= startDate && date <= endDate) {
              const companyName = t.company_name || t.deal_name || 'Unknown'
              allEvents.push({
                id: `task-${t.id}`,
                dealId: t.deal_id ?? 0,
                dealName: t.deal_name || companyName,
                companyName,
                title: t.description,
                type: 'task',
                date,
                metadata: {
                  assignedTo: t.assigned_to || undefined,
                  template: t.template,
                },
              })
            }
          })
      }
    } catch (error) {
      console.error('Error fetching calendar tasks:', error)
    }

    return allEvents
  }




  const getEventIcon = (type: CalendarEvent['type']) => {
    switch (type) {
      case 'go_live':
        return <Rocket className="w-3 h-3" />
      case 'production_access':
        return <Key className="w-3 h-3" />
      case 'custom_work':
        return <Wrench className="w-3 h-3" />
      case 'deliverable':
        return <Package className="w-3 h-3" />
      case 'task':
        return <CheckSquare className="w-3 h-3" />
    }
  }

  const getEventColor = (type: CalendarEvent['type']) => {
    switch (type) {
      case 'go_live':
        return 'bg-green-500/15 border-green-500/40 text-green-400'
      case 'production_access':
        return 'bg-blue-500/15 border-blue-500/40 text-blue-400'
      case 'custom_work':
        return 'bg-purple-500/15 border-purple-500/40 text-purple-400'
      case 'deliverable':
        return 'bg-orange-500/15 border-orange-500/40 text-orange-400'
      case 'task':
        return 'bg-rose-500/15 border-rose-500/40 text-rose-400'
    }
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="text-sm text-[var(--text-muted)] mt-2">Loading calendar data...</p>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="text-center py-12">
        <CalendarIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Unable to Load Calendar</h3>
        <p className="text-sm text-[var(--text-muted)] mb-4">
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary/90 transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  // Show empty state if no events exist at all
  if (events.length === 0) {
    return (
      <div className="text-center py-12">
        <CalendarIcon className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">No Events Found</h3>
        <p className="text-sm text-[var(--text-muted)] mb-4">
          No go-live dates, production access dates, custom work items, or tasks with due dates have been configured yet.
        </p>
        <p className="text-xs text-[var(--text-muted)]">
          Add dates to deal scopes in the Scope tab to see them appear here.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col" style={{ minHeight: 'calc(100vh - 220px)' }}>
      {/* Header with Controls */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">Upcoming Events Calendar</h2>
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-1 ml-7">
            Key upcoming dates — production keys, go-lives, and custom feature deadlines. Automatic Slack notifications can be configured via Settings.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-2 text-sm bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
            onClick={handleExportPDF}
          >
            Export PDF
          </button>
          <button
            className="px-3 py-2 text-sm bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
            onClick={() => navigate('/dashboard/presales/tools/scheduler')}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Scheduler
          </button>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 border-l-4 border-l-blue-500 shadow-sm mb-4">
        <button
          className="p-2 hover:bg-slate-700 rounded transition-colors text-slate-100"
          onClick={handlePreviousMonth}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-4">
          <h3 className="text-xl font-semibold text-slate-100">
            {selectedMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </h3>
          <button
            className="px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primary/90 transition-colors"
            onClick={handleToday}
          >
            Today
          </button>
          <div className="flex items-center gap-2 ml-4">
            <button
              className={`px-3 py-1 text-sm rounded transition-colors ${viewMode === 'calendar' ? 'bg-primary text-white' : 'bg-slate-700 text-slate-200 hover:bg-slate-600'}`}
              onClick={() => setViewMode('calendar')}
            >
              Calendar
            </button>
            <button
              className={`px-3 py-1 text-sm rounded transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-slate-700 text-slate-200 hover:bg-slate-600'}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
          </div>
        </div>

        <button
          className="p-2 hover:bg-slate-700 rounded transition-colors text-slate-100"
          onClick={handleNextMonth}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs mb-4">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-100 border border-green-400 shadow-sm"></div>
          <span className="font-medium">Go Live</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-100 border border-blue-400 shadow-sm"></div>
          <span className="font-medium">Production Keys</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-purple-100 border border-purple-400 shadow-sm"></div>
          <span className="font-medium">Custom Work</span>
          <button
            onClick={() => setHideHandedOverCustomWork((v) => !v)}
            className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
              hideHandedOverCustomWork
                ? 'bg-slate-700 border-slate-600 text-slate-400 hover:text-slate-200'
                : 'bg-purple-900/30 border-purple-600/50 text-purple-300'
            }`}
            title={hideHandedOverCustomWork ? 'Handed-over SOW items hidden — click to show' : 'Showing handed-over SOW items — click to hide'}
          >
            {hideHandedOverCustomWork ? 'impl hidden' : 'impl shown'}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-orange-100 border border-orange-400 shadow-sm"></div>
          <span className="font-medium">Deliverable</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-rose-100 border border-rose-400 shadow-sm"></div>
          <span className="font-medium">Task</span>
        </div>
        <div className="ml-auto text-[var(--text-muted)] font-medium">{monthEvents.length} events this month</div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' ? (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg overflow-hidden shadow-sm flex-1 flex flex-col">
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 border-b border-[var(--border-color)]">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="p-2.5 text-center text-sm font-semibold text-[var(--text-muted)] bg-[var(--bg-tertiary)]">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 flex-1">
            {calendarDays.map((day, index) => {
              const isToday =
                day.date.toDateString() === new Date().toDateString() && day.isCurrentMonth
              const dateKey = day.date.toISOString().split('T')[0]
              const isExpanded = expandedDay === dateKey
              const maxVisible = 5
              return (
                <div
                  key={index}
                  className={`min-h-[120px] lg:min-h-[160px] p-2 border-r border-b border-[var(--border-color)] relative transition-colors overflow-visible ${
                    !day.isCurrentMonth ? 'bg-[var(--bg-tertiary)] opacity-40' : ''
                  } ${isToday ? 'bg-primary/5 ring-1 ring-primary/20 ring-inset' : ''}`}
                >
                  <div
                    className={`text-sm font-medium mb-1.5 ${
                      isToday
                        ? 'text-white font-bold bg-primary w-7 h-7 rounded-full flex items-center justify-center -mt-0.5'
                        : 'text-[var(--text-secondary)]'
                    }`}
                  >
                    {day.date.getDate()}
                  </div>
                  <div className="space-y-1">
                    {day.events.slice(0, isExpanded ? undefined : maxVisible).map((event) => (
                      <div
                        key={event.id}
                        className={`group/event relative text-xs leading-tight px-1.5 py-1 rounded border shadow-sm cursor-pointer hover:brightness-110 hover:shadow-md hover:z-[100] transition-all ${getEventColor(event.type)}`}
                        onClick={() => handleEventClick(event)}
                      >
                        <div className="flex items-center gap-1">
                          <span className="inline-flex flex-shrink-0">{getEventIcon(event.type)}</span>
                          <span className="truncate font-semibold">
                            {event.companyName.length > 20 ? event.companyName.substring(0, 20) + '...' : event.companyName}
                          </span>
                          {event.metadata?.isHardDate && (
                            <span className="flex-shrink-0 text-[8px] font-bold text-red-600" title="Hard Date">⚠️</span>
                          )}
                        </div>
                        {/* Custom Tooltip */}
                        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 invisible group-hover/event:opacity-100 group-hover/event:visible transition-all duration-150 pointer-events-none">
                          <div className="bg-gray-900 text-white text-xs rounded-lg shadow-xl px-3 py-2.5 whitespace-nowrap max-w-xs">
                            <div className="font-bold text-sm mb-1">{event.companyName}</div>
                            <div className="text-gray-300">{event.title}</div>
                            {event.metadata?.isHardDate && (
                              <div className="text-red-400 font-bold mt-1">⚠️ HARD DATE</div>
                            )}
                            {event.metadata?.assignedTo && (
                              <div className="text-gray-400 mt-1">Assigned: {event.metadata.assignedTo}</div>
                            )}
                            <div className="text-gray-400 mt-1">
                              {event.date.toLocaleDateString('default', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </div>
                            {/* Arrow */}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-gray-900" />
                          </div>
                        </div>
                      </div>
                    ))}
                    {!isExpanded && day.events.length > maxVisible && (
                      <button
                        className="text-xs text-primary font-medium pl-1 hover:underline cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation()
                          setExpandedDay(dateKey)
                        }}
                      >
                        +{day.events.length - maxVisible} more
                      </button>
                    )}
                    {isExpanded && day.events.length > maxVisible && (
                      <button
                        className="text-xs text-primary font-medium pl-1 hover:underline cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation()
                          setExpandedDay(null)
                        }}
                      >
                        Show less
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        /* List View */
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg divide-y divide-[var(--border-color)] shadow-sm flex-1">
          {monthEvents.length === 0 ? (
            <div className="p-8 text-center text-[var(--text-muted)]">
              No events scheduled for this month
            </div>
          ) : (
            monthEvents.map((event) => (
              <div
                key={event.id}
                className="p-4 hover:bg-[var(--bg-tertiary)] transition-all cursor-pointer group"
                onClick={() => handleEventClick(event)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded border text-xs font-medium shadow-sm ${getEventColor(event.type)}`}>
                        {getEventIcon(event.type)}
                        {event.type.replace('_', ' ').toUpperCase()}
                      </span>
                      {event.metadata?.isHardDate && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-red-500/15 text-red-400 border border-red-500/30">
                          ⚠️ Hard Date
                        </span>
                      )}
                      <span className="text-sm font-semibold text-[var(--text-primary)] group-hover:text-primary transition-colors">
                        {event.companyName}
                      </span>
                    </div>
                    <p className="text-sm text-[var(--text-secondary)] mt-1 font-medium">{event.title}</p>
                    {event.description && (
                      <p className="text-sm text-[var(--text-muted)] mt-0.5">{event.description}</p>
                    )}
                    {event.metadata?.assignedTo && (
                      <p className="text-xs text-[var(--text-muted)] mt-1">Assigned to {event.metadata.assignedTo}</p>
                    )}
                    {event.metadata?.jiraTickets && event.metadata.jiraTickets.length > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-[var(--text-muted)]">Jira:</span>
                        {event.metadata.jiraTickets.map((ticket, idx) => (
                          <span
                            key={idx}
                            className="text-xs px-2 py-0.5 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded font-mono"
                          >
                            {ticket.ticket}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-[var(--text-primary)]">
                      {event.date.toLocaleDateString('default', { month: 'short', day: 'numeric' })}
                    </div>
                    <div className="text-xs text-[var(--text-muted)] mt-1 font-medium">
                      {event.date.toLocaleDateString('default', { weekday: 'short' })}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

    </div>
  )
}
